/**
 * The `Profile` panel — `04-officials-profiles.md` §B2, drawn to the agreed mockup.
 *
 * **Conformance note (run 21C).** The mockup — `docs/design/official-profile-detail-v1.html`
 * — draws this panel as an `.idcard`: a 174px initials block on the left under the caption
 * `Photo Placeholder Until Intake Completes`, and a two-column `.idfields` grid of nine
 * labelled fields on the right, with `Media Name` spanning both columns. The earlier build
 * borrowed the marking screen's `.markhead` strip instead, which put the same nine facts on
 * the page in a shape the Technical Delegate had not agreed. The mockup wins on visuals, so
 * the panel is now the mockup's.
 *
 * §B2's field table, in its order and with its labels verbatim: `Given Names`, `Surnames`,
 * `Media Name`, `Role For This Tournament`, `Country`, `Gender`, `Date Of Birth`, `Email
 * Address`, `Mobile Number`. Nine fields, no tenth — the spec is explicit that **there is no
 * WhatsApp field anywhere in either mockup** and that contact is email plus mobile only.
 *
 * **Where the personal data comes from.** All of it travels on `GET /officials/:tmsPersonId`,
 * which is `requireTd`-guarded precisely because it carries a date of birth and contact
 * details; the roster list projection strips them, so a table of forty-eight people cannot
 * broadcast forty-eight telephone numbers as a side effect of rendering.
 *
 * **The two places the mockup shows more than this build holds, and what is drawn instead.**
 * `Country` reads `NED · Netherlands` in the mockup; no country-name table exists in this
 * build, so the code is shown beside its flag (the shared `CountryFlag`) and the name is not
 * guessed. Pre-Tournament Duties (§B2, ruling CC-Q7(c)) is now a real, editable field — see
 * `PreTournamentDutiesField` below; the mockup has no such field, so this is the spec adding
 * to it.
 *
 * **The age in parentheses is now shown**, as both §B2 and the mockup ask (`22 Mar 1984
 * (42)`). It is computed from the date of birth against today, which is what a reader means
 * by an age; the recorded date beside it is the fact, and the age is the reading of it.
 */

import { useEffect, useState } from "react";

import {
  holdsPreTournamentDuties,
  PRE_TOURNAMENT_DUTY_VALUES,
  UMPIRE_TYPES,
  umpireTypeLabel,
  type DateOnly,
  type PreTournamentDutyValue,
} from "@hockeytd/shared";

import {
  putOfficialAvoidVu,
  putOfficialCrossGender,
  putOfficialDuties,
  putOfficialUmpireType,
} from "../../api/client.js";
import type { OfficialDetailView } from "../../api/types.js";
import { CountryFlag } from "../../app/CountryFlag.js";
import { tournamentClockParts } from "../../app/tournamentTime.js";
import { formatDateOfBirth } from "../marking/MarkingHeader.js";
import { initialsOf } from "./initials.js";
import { useOfficialPhoto } from "./useOfficialPhoto.js";

const GENDER_LABEL: Readonly<Record<string, string>> = { m: "Male", f: "Female" };

/**
 * Whole years between a `YYYY-MM-DD` and today, or null when the date is unusable.
 *
 * **RUN 83 — "today" is the tournament's day**, not the device's. This is the mildest of the
 * device reads the sweep found, and it is still a read: an official whose birthday is today is
 * a year younger on a laptop west of the host city than on the TD's beside it, and the number
 * on a profile ought not to depend on who has it open. The birth date itself is a calendar day
 * and is left alone — it is parsed from its own digits and never re-zoned.
 */
export function ageFrom(value: DateOnly | null | undefined, now: Date = new Date()): number | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const today = tournamentClockParts(now);
  let age = today.year - year;
  if (today.month < month || (today.month === month && today.day < day)) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/** A field with no value on the record. Absent is a fact; blank would look like a bug. */
function NotRecorded({ what }: { what: string }): React.JSX.Element {
  return (
    <span className="cell-none" title={`No ${what} on this official's record`}>
      Not Recorded
    </span>
  );
}

function Field({
  label,
  mono = false,
  wide = false,
  className,
  children,
}: {
  label: string;
  mono?: boolean;
  wide?: boolean;
  className?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={`idfield${wide ? " idfield--wide" : ""}`}>
      <span className="k">{label}</span>
      <span className={`v${mono ? " mono" : ""}${className === undefined ? "" : ` ${className}`}`}>
        {children}
      </span>
    </div>
  );
}

/**
 * §B2's `Pre-Tournament Duties` — the field, and the editor behind it (run 30).
 *
 * **Ruling CC-Q7(c), 2026-08-11.** The stations are four PRE-DEFINED checkboxes — `Passport`,
 * `Uniform`, `Stick`, `GK & PC Equipment` — not free text and not configurable, and an official
 * may hold more than one. The additional responsibilities, usually an ATD's, are the same enum's
 * remaining five values. They are rendered in the enum's own order, which is the order the
 * ruling lists them.
 *
 * **Two roles never see the editor at all.** Spec 04 §B2: *"Only TD, ATDs, TOs and Judges do
 * pre-tournament checks; UMs and Umpires are not involved at all."* For those two the field
 * stays static and says so in a short line, rather than offering nine boxes that the route
 * would refuse with a 422. The refusal still exists — it is the backstop, not the explanation.
 *
 * **Assignment is purely manual and there is nothing to balance against**, so the editor has no
 * suggestion, no warning and no conflict check. It records what the Technical Delegate decided.
 *
 * **Save is REPLACE.** The whole ticked set is posted and exactly that set is stored; unticking
 * the last box clears the field, which is a save rather than a no-op. The panel shows what came
 * back from the server, not what it sent, so a divergence is visible rather than assumed away.
 */
function PreTournamentDutiesField({
  official,
}: {
  official: OfficialDetailView;
}): React.JSX.Element | null {
  const eligible = holdsPreTournamentDuties(official.role);
  const [stored, setStored] = useState<PreTournamentDutyValue[]>(official.preTournamentDuties);
  const [ticked, setTicked] = useState<PreTournamentDutyValue[]>(official.preTournamentDuties);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A different official is a different assignment. Without this the panel would carry one
  // person's stations onto the next profile the TD opened.
  useEffect(() => {
    setStored(official.preTournamentDuties);
    setTicked(official.preTournamentDuties);
    setEditing(false);
    setFailure(null);
  }, [official.tmsPersonId, official.preTournamentDuties]);

  // An ineligible role never reaches this component any more — its whole row is withheld
  // above (Alfred, 2026-09-03). The guard stays as a belt for a future second mount point.
  if (!eligible) return null;

  if (!editing) {
    return (
      <span className="dutyfield" data-testid="duties-field">
        {stored.length === 0 ? (
          <span className="cell-none">None Recorded</span>
        ) : (
          <span className="dutylist">
            {stored.map((duty) => (
              <span className="dutytag" key={duty}>
                {duty}
              </span>
            ))}
          </span>
        )}
        <button
          type="button"
          className="linkbtn dutyedit"
          data-testid="duties-edit"
          onClick={() => {
            setTicked(stored);
            setFailure(null);
            setEditing(true);
          }}
        >
          Edit Duties
        </button>
      </span>
    );
  }

  const toggle = (duty: PreTournamentDutyValue): void => {
    setTicked((current) =>
      current.includes(duty) ? current.filter((held) => held !== duty) : [...current, duty],
    );
  };

  const save = (): void => {
    setSaving(true);
    setFailure(null);
    putOfficialDuties(official.tmsPersonId, ticked).then(
      (body) => {
        // What the server stored, not what this screen sent.
        setStored(body.preTournamentDuties);
        setTicked(body.preTournamentDuties);
        setSaving(false);
        setEditing(false);
      },
      (error: unknown) => {
        setSaving(false);
        setFailure(error instanceof Error ? error.message : "The duties could not be saved.");
      },
    );
  };

  return (
    <span className="dutyeditor" data-testid="duties-editor">
      <fieldset className="dutyboxes">
        <legend className="dutyboxes__legend">Pre-Tournament Duties</legend>
        {PRE_TOURNAMENT_DUTY_VALUES.map((duty) => (
          <label className="dutybox" key={duty}>
            <input
              type="checkbox"
              checked={ticked.includes(duty)}
              disabled={saving}
              onChange={() => {
                toggle(duty);
              }}
            />
            {duty}
          </label>
        ))}
      </fieldset>

      <span className="dutyactions">
        <button
          type="button"
          className="linkbtn"
          disabled={saving}
          data-testid="duties-save"
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="linkbtn"
          disabled={saving}
          onClick={() => {
            setTicked(stored);
            setFailure(null);
            setEditing(false);
          }}
        >
          Cancel
        </button>
      </span>

      {failure === null ? null : (
        <span className="dutyfail" role="alert">
          {failure}
        </span>
      )}
    </span>
  );
}

/** One offered choice: the value the route stores and the word the field prints for it. */
type ChoiceOption<Value extends string> = { value: Value; label: string };

/**
 * The three umpire-only drafting attributes share one piece of state-and-save plumbing — this
 * hook. It is `ChoiceField`'s old body (run 41) with the summary and the editor pulled apart
 * (run 49), because Alfred's ask — "put them all on one row, and then have a small Edit button
 * under each one … when the button is clicked, the options and the save/cancel buttons show
 * just below" — draws the collapsed tag+button and the open editor in two different PLACES on
 * the page, not two different states of the same box. The hook still owns the one true state
 * machine (`stored`, `chosen`, `saving`, `failure`); which state is called "open" now lives with
 * the caller, via `open`/`onOpenChange`, so that `UmpireAttributesRow` below can hold ONE open
 * flag for all three fields instead of three independent ones (see that component's doc comment
 * for why only one editor is ever open at a time).
 *
 * **Save posts the value whole and draws what came back**, not what the radio was set to — the
 * same server-truth rule the duties field follows, so a divergence shows rather than being
 * assumed away.
 */
function useChoiceField<Value extends string>({
  official,
  stored: storedValue,
  save: saveValue,
  failureMessage,
  onOpenChange,
}: {
  official: OfficialDetailView;
  stored: Value;
  save: (value: Value) => Promise<Value>;
  failureMessage: string;
  /** Called with `true`/`false` as this field's own editor opens or closes — see the row's doc
   *  comment for why "open" is owned by the caller rather than this hook. */
  onOpenChange: (open: boolean) => void;
}): {
  stored: Value;
  chosen: Value;
  setChosen: (value: Value) => void;
  saving: boolean;
  failure: string | null;
  startEdit: () => void;
  cancelEdit: () => void;
  doSave: () => void;
} {
  const [stored, setStored] = useState<Value>(storedValue);
  const [chosen, setChosen] = useState<Value>(storedValue);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A different official is a different setting. Without this the panel would carry one
  // umpire's answer onto the next profile the TD opened.
  useEffect(() => {
    setStored(storedValue);
    setChosen(storedValue);
    onOpenChange(false);
    setFailure(null);
    // `onOpenChange` closes over the field's own name and is re-created every render of the
    // row; it is not a dependency the reset needs to react to, only the person and their
    // stored answer are.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [official.tmsPersonId, storedValue]);

  return {
    stored,
    chosen,
    setChosen,
    saving,
    failure,
    startEdit: (): void => {
      setChosen(stored);
      setFailure(null);
      onOpenChange(true);
    },
    cancelEdit: (): void => {
      setChosen(stored);
      setFailure(null);
      onOpenChange(false);
    },
    doSave: (): void => {
      setSaving(true);
      setFailure(null);
      saveValue(chosen).then(
        (value) => {
          // What the server stored, not what this screen sent.
          setStored(value);
          setChosen(value);
          setSaving(false);
          onOpenChange(false);
        },
        (error: unknown) => {
          setSaving(false);
          setFailure(error instanceof Error ? error.message : failureMessage);
        },
      );
    },
  };
}

/**
 * The collapsed state of one attribute — the value tag, and underneath it (run 49's layout) a
 * small `Edit` button. The button's own text stays just `Edit`, because three columns wide the
 * card already carries the caption above it; but the accessible name must still tell "Edit" on
 * the Cross Gender column from "Edit" on the VU column, so `aria-label` carries the fuller
 * `editLabel` a screen reader announces instead.
 */
function ChoiceSummary<Value extends string>({
  testId,
  editLabel,
  options,
  value,
  onEdit,
}: {
  testId: string;
  editLabel: string;
  options: readonly ChoiceOption<Value>[];
  value: Value;
  onEdit: () => void;
}): React.JSX.Element {
  const label = options.find((option) => option.value === value)?.label ?? value;
  return (
    <span className="dutyfield" data-testid={`${testId}-field`}>
      <span className="dutytag">{label}</span>
      <button
        type="button"
        className="linkbtn dutyedit"
        data-testid={`${testId}-edit`}
        aria-label={editLabel}
        onClick={onEdit}
      >
        Edit
      </button>
    </span>
  );
}

/**
 * The open editor for one attribute — unchanged from run 41's fieldset of radios plus
 * Save/Cancel, just drawn by `UmpireAttributesRow` below the three columns rather than inside
 * whichever one it belongs to. Three-wide, `Umpire Type`'s trio of options would be squeezed
 * into a column barely wider than one word; full row width is the whole reason that placement
 * was chosen (see that component's doc comment).
 */
function ChoiceEditor<Value extends string>({
  testId,
  legend,
  groupName,
  options,
  chosen,
  onChoose,
  saving,
  failure,
  onSave,
  onCancel,
}: {
  testId: string;
  legend: string;
  groupName: string;
  options: readonly ChoiceOption<Value>[];
  chosen: Value;
  onChoose: (value: Value) => void;
  saving: boolean;
  failure: string | null;
  onSave: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <span className="dutyeditor" data-testid={`${testId}-editor`}>
      <fieldset className="dutyboxes">
        <legend className="dutyboxes__legend">{legend}</legend>
        {options.map((option) => (
          <label className="dutybox" key={option.value}>
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={chosen === option.value}
              disabled={saving}
              onChange={() => {
                onChoose(option.value);
              }}
            />
            {option.label}
          </label>
        ))}
      </fieldset>

      <span className="dutyactions">
        <button
          type="button"
          className="linkbtn"
          disabled={saving}
          data-testid={`${testId}-save`}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="linkbtn" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </span>

      {failure === null ? null : (
        <span className="dutyfail" role="alert">
          {failure}
        </span>
      )}
    </span>
  );
}

/**
 * §B2's three umpire-only drafting attributes, laid out as ONE ROW (run 49).
 *
 * **The ask, near-verbatim (Alfred, 2026-09-03).** *"Put them all on one row, and then have a
 * small Edit button under each one instead. And when the button is clicked, the options and the
 * save/cancel buttons show just below. This layout should also be responsive and mobile
 * friendly."* Three columns — `Cross Gender`, `VU`, `Umpire Type` — each a caption, a value tag
 * and a small `Edit` button; the row renders nothing at all for a non-Umpire, the same as each
 * of the three did on its own before this run, so it costs a non-Umpire's card no space and no
 * stray gap.
 *
 * **Where the opened editor is drawn.** Three columns across the fields grid is roughly a third
 * of `.idcard`'s right-hand side — comfortably wide enough for a `Yes / No` pair, but `Umpire
 * Type`'s `Leading / General / Upcoming` trio would be squeezed into a column not much wider
 * than the longest of those three words. Rather than one field's editor behaving differently
 * from the other two depending on how much room its column happens to have, EVERY editor here
 * draws below the row of three columns, at the row's full width — the same choice made once
 * rather than two different layouts for three fields that are otherwise identical.
 *
 * **One editor open at a time.** The three fields used to hold their own `editing` flag
 * independently, so nothing stopped a TD opening all three at once. Lifting that flag up to
 * this component — `open`, one of `"crossgender" | "avoidvu" | "umpiretype" | null` — was a
 * small change once the editor left each field's own box, and the three `useChoiceField` calls
 * below simply report their opening and closing through it rather than owning it themselves.
 *
 * **Responsive.** Below the app's existing 720px breakpoint (`.idcard`/`.idfields`, `editorial.css`)
 * the three columns stack to one, same as every other field on this card; the row and its
 * editor never scroll sideways at phone widths. See the `RUN 49` block at the foot of
 * `editorial.css` for the rules themselves.
 *
 * **What each of the three records, kept from run 41 and untouched by this reshuffle:**
 *
 *   · **`Cross Gender`** — `No` keeps this umpire off every slot (U1, U2, RU, VU) on a match of
 *     the opposite gender to their own, and has no effect where that gender is not recorded.
 *   · **`VU`** — `No` means the draft never proposes this umpire as Video Umpire; every other
 *     slot is unaffected. THIS IS `avoid_vu`, INVERTED, one `!` on the way in and one on the way
 *     out, so no other layer has to know which way up the stored value is (run 33/41).
 *   · **`Umpire Type`** — `Leading` pairs with anyone, `General` may not pair with `Upcoming`,
 *     `Upcoming` pairs only with `Leading`. Not a grading; `Official Experience` below carries
 *     TMS's record of a career, this is the Technical Delegate's judgement about this panel.
 *
 * **Save posts the value whole and draws what came back**, not what the radio was set to, on
 * all three — the same server-truth rule the duties field follows.
 */
function UmpireAttributesRow({ official }: { official: OfficialDetailView }): React.JSX.Element | null {
  type Stem = "crossgender" | "avoidvu" | "umpiretype";
  const [open, setOpen] = useState<Stem | null>(null);
  const openOnly = (stem: Stem) => (isOpen: boolean): void => setOpen(isOpen ? stem : null);

  const crossGenderOptions: readonly ChoiceOption<"yes" | "no">[] = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  const vuOptions: readonly ChoiceOption<"yes" | "no">[] = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  const umpireTypeOptions = UMPIRE_TYPES.map((value) => ({ value, label: umpireTypeLabel(value) }));

  const crossGender = useChoiceField({
    official,
    stored: official.crossGender ? "yes" : "no",
    save: async (value) =>
      (await putOfficialCrossGender(official.tmsPersonId, value === "yes")).crossGender
        ? "yes"
        : "no",
    failureMessage: "The Cross Gender setting could not be saved.",
    onOpenChange: openOnly("crossgender"),
  });
  const vu = useChoiceField({
    official,
    stored: official.avoidVu ? "no" : "yes",
    save: async (value) =>
      (await putOfficialAvoidVu(official.tmsPersonId, value === "no")).avoidVu ? "no" : "yes",
    failureMessage: "The VU setting could not be saved.",
    onOpenChange: openOnly("avoidvu"),
  });
  const umpireType = useChoiceField({
    official,
    stored: official.umpireType,
    save: async (value) => (await putOfficialUmpireType(official.tmsPersonId, value)).umpireType,
    failureMessage: "The Umpire Type could not be saved.",
    onOpenChange: openOnly("umpiretype"),
  });

  if (official.role !== "Umpire") return null;

  return (
    <div className="idfield idfield--wide umpireattrs">
      <div className="umpireattrs__cols">
        <div className="umpireattrs__col">
          <span className="k">Cross Gender</span>
          <ChoiceSummary
            testId="crossgender"
            editLabel="Edit Cross Gender"
            options={crossGenderOptions}
            value={crossGender.stored}
            onEdit={crossGender.startEdit}
          />
        </div>
        <div className="umpireattrs__col">
          <span className="k">VU</span>
          <ChoiceSummary
            testId="avoidvu"
            editLabel="Edit VU"
            options={vuOptions}
            value={vu.stored}
            onEdit={vu.startEdit}
          />
        </div>
        <div className="umpireattrs__col">
          <span className="k">Umpire Type</span>
          <ChoiceSummary
            testId="umpiretype"
            editLabel="Edit Umpire Type"
            options={umpireTypeOptions}
            value={umpireType.stored}
            onEdit={umpireType.startEdit}
          />
        </div>
      </div>

      {open !== "crossgender" ? null : (
        <ChoiceEditor
          testId="crossgender"
          legend="Cross Gender"
          groupName={`crossgender-${official.tmsPersonId}`}
          options={crossGenderOptions}
          chosen={crossGender.chosen}
          onChoose={crossGender.setChosen}
          saving={crossGender.saving}
          failure={crossGender.failure}
          onSave={crossGender.doSave}
          onCancel={crossGender.cancelEdit}
        />
      )}
      {open !== "avoidvu" ? null : (
        <ChoiceEditor
          testId="avoidvu"
          legend="VU"
          groupName={`avoidvu-${official.tmsPersonId}`}
          options={vuOptions}
          chosen={vu.chosen}
          onChoose={vu.setChosen}
          saving={vu.saving}
          failure={vu.failure}
          onSave={vu.doSave}
          onCancel={vu.cancelEdit}
        />
      )}
      {open !== "umpiretype" ? null : (
        <ChoiceEditor
          testId="umpiretype"
          legend="Umpire Type"
          groupName={`umpiretype-${official.tmsPersonId}`}
          options={umpireTypeOptions}
          chosen={umpireType.chosen}
          onChoose={umpireType.setChosen}
          saving={umpireType.saving}
          failure={umpireType.failure}
          onSave={umpireType.doSave}
          onCancel={umpireType.cancelEdit}
        />
      )}
    </div>
  );
}

export function ProfilePanel({
  official,
}: {
  official: OfficialDetailView;
}): React.JSX.Element {
  const bornOn = formatDateOfBirth(official.dateOfBirth);
  const age = ageFrom(official.dateOfBirth);
  const photo = useOfficialPhoto(official.tmsPersonId, official.photoRef);

  return (
    <section className="panel panel--d1" aria-labelledby="profileTitle" data-howto="profile-panel">
      <div className="panel__head">
        <h3 className="panel__title" id="profileTitle">
          Profile
        </h3>
        <span className="spacer" />
        <span className="panel__note">Source · Officials Profile Intake</span>
      </div>

      <div className="idcard">
        {/*
          The photograph, or the initials block that stood in for it before there were any.

          Three things are deliberate here. The caption goes AWAY when there is a photograph —
          it says the intake has not completed, and for this person it has. The `<img>` is
          drawn only once the object URL exists, so the card never shows a blank square while
          a fetch is in flight; the placeholder holds the space until the face arrives, and
          keeps it for good if the fetch fails (see `useOfficialPhoto`). And `object-fit:
          cover` on a square block is what makes the panel survive the photographs that were
          not square — the instructions asked for 1:1 and the compliance record exists
          precisely because not everybody sent one.
        */}
        <div>
          {photo.url === null ? (
            <>
              <div
                className={`bigavatar${official.gender === "f" ? " bigavatar--f" : ""}`}
                aria-hidden="true"
                data-testid="profile-initials"
              >
                {initialsOf(official.mediaName)}
              </div>
              <span className="bigavatar__cap">Photo Placeholder Until Intake Completes</span>
            </>
          ) : (
            <div className="bigavatar bigavatar--photo">
              <img
                className="bigavatar__img"
                src={photo.url}
                alt={`Photograph of ${official.mediaName}`}
                data-testid="profile-photo"
              />
            </div>
          )}
        </div>

        <div className="idfields" data-testid="profile-fields">
          <Field label="Given Names">{official.givenNames}</Field>
          <Field label="Surnames">{official.surnames}</Field>
          <Field label="Media Name" wide className={official.gender === null ? "" : `nm-${official.gender}`}>
            {official.mediaName}
          </Field>
          <Field label="Role For This Tournament">{official.role}</Field>
          <Field label="Country" mono>
            <CountryFlag code={official.country} />
          </Field>
          <Field label="Gender">
            {official.gender === null ? (
              <NotRecorded what="gender" />
            ) : (
              GENDER_LABEL[official.gender] ?? official.gender
            )}
          </Field>
          <Field label="Date Of Birth" mono>
            <span data-testid="profile-dob">
              {bornOn === null ? (
                <NotRecorded what="date of birth" />
              ) : (
                <>
                  {bornOn}
                  {age === null ? null : <span style={{ color: "var(--ink-3)" }}> ({age})</span>}
                </>
              )}
            </span>
          </Field>
          <Field label="Email Address" mono>
            {official.email === null ? (
              <NotRecorded what="email address" />
            ) : (
              <a
                className="maillink"
                href={`mailto:${official.email}`}
                title={`Write To ${official.mediaName}`}
              >
                {official.email}
              </a>
            )}
          </Field>
          <Field label="Mobile Number" mono>
            {official.mobile === null ? <NotRecorded what="mobile number" /> : official.mobile}
          </Field>
          {/*
            Roles that hold no pre-tournament duty get no row at all (Alfred, 2026-09-03) —
            the earlier `Not Applicable To This Role` explainer told an umpire's reader about
            other people's work, which is the footer's job, not a field's.
          */}
          {holdsPreTournamentDuties(official.role) ? (
            <Field label="Pre-Tournament Duties" wide>
              <PreTournamentDutiesField official={official} />
            </Field>
          ) : null}
          {/*
            `UmpireAttributesRow` draws its own captions (run 49's one-row-three-columns layout)
            and its own `idfield--wide` wrapper, and renders nothing at all for a non-Umpire —
            so, exactly as before this run, no caption stands over an empty value on any Judge,
            TO or UM card.

            The order is the order the Technical Delegate asked for them in, and it reads from
            the widest rule to the narrowest: Cross Gender governs every slot, VU governs one,
            and Umpire Type governs who stands beside whom on the two that are left.
          */}
          <UmpireAttributesRow official={official} />
        </div>
      </div>

      <div className="panel__foot">
        <b>Contact is email and mobile only —</b> the mobile number is the one the official
        gave on the intake form, and it is the number to use on WhatsApp as well. Photographs
        arrive with that form; there is deliberately nothing to upload here.{" "}
        <b>Pre-Tournament Duties</b> are the four station checks — Passport, Uniform, Stick,
        GK &amp; PC Equipment — plus any additional duty. Assignment is purely manual: there is
        nothing to balance against, so the system records the decision rather than suggesting
        one, and an official may hold more than one station. Only the Technical Delegate,
        Assistant Technical Delegates, Technical Officers and Judges hold duties at all.{" "}
        <b>Cross Gender</b>, <b>VU</b> and <b>Umpire Type</b> are umpire-only settings, and all
        three steer the AI draft for Umpire Appointments alone — they change nothing about what
        the Umpires Managers do in TMS. <b>Cross Gender: No</b> keeps this umpire off matches of
        the opposite gender in every slot, and has no effect at all where the gender above is
        Not Recorded. <b>VU: No</b> means the draft never proposes this umpire as Video Umpire;
        every other slot is unaffected. <b>Umpire Type</b> decides who may stand on the pitch
        together — a Leading umpire pairs with anyone, a General umpire is not paired with an
        Upcoming one, and an Upcoming umpire is paired only with a Leading one. It is the
        Technical Delegate's judgement about this panel and not a grading; TMS's record of a
        career is in Official Experience below.
      </div>
    </section>
  );
}
